'use client'

import { useTranslations } from 'next-intl'

import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useProjectStore } from '@/hooks/use-project-store'
import PricingPage from '../pricing-v3'

export default function PricingModal({
  type,
  projectId,
  minRequiredPlan,
  workspaceId,
  trigger,
  domainLimits,
}: {
  type?:
    | 'out_of_credit'
    | 'need_basic'
    | 'need_premium'
    | 'need_premium_for_payments'
    | 'need_platinum'
    | 'recommend_premium'
    | 'intro_new_plan'
  trigger?: string
  projectId?: string
  workspaceId?: string
  minRequiredPlan?: 'basic' | 'premium' | 'platinum'
  domainLimits?: {
    max_domains: number
    current_domains: number
    remaining_domains: number
    can_create_more: boolean
  }
} = {}) {
  const t = useTranslations('pricing')
  const { project } = useProjectStore()

  // Handle workspace member management specific messaging
  const isWorkspaceMemberManagement = trigger === 'workspace-member-management'
  const title = isWorkspaceMemberManagement
    ? t('need_premium.workspace_member_management.title')
    : type
      ? t(`${type}.title`)
      : t('title')
  const description = isWorkspaceMemberManagement
    ? t('need_premium.workspace_member_management.description')
    : type
      ? t(`${type}.description`)
      : t('description')

  // Show domain limit explanation if need_platinum and domain limit is reached
  const showDomainLimitExplanation =
    type === 'need_platinum' &&
    domainLimits &&
    !domainLimits.can_create_more &&
    (trigger === 'custom-domain' || trigger === 'buy-domain')
  // Prefer explicit projectId from caller, otherwise fall back to the project in store
  const selectedProjectId = projectId ?? project?._id?.toString()

  // Derive minRequiredPlan from type if not explicitly provided
  const derivedMinRequiredPlan =
    minRequiredPlan ||
    (() => {
      switch (type) {
        case 'need_basic':
          return 'basic'
        case 'need_premium':
        case 'need_premium_for_payments':
          return 'premium'
        case 'need_platinum':
          return 'platinum'
        default:
          return undefined
      }
    })()

  return (
    <DialogContent className="w-[95%] max-w-[1400px] p-0 rounded-[20px] overflow-hidden border-none">
      <div className="relative w-full h-full">
        <div className="relative m-[1px] rounded-[20px] p-2 sm:p-6 max-w-full overflow-hidden">
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-xl">{title}</DialogTitle>
            <DialogDescription className="pb-4 whitespace-pre-line leading-loose">
              {description}
            </DialogDescription>
            {showDomainLimitExplanation && (
              <div className="mt-4 p-4 bg-[#FDD577]/10 border border-[#FDD577]/20 rounded-lg">
                <p className="text-sm font-semibold text-[#FDD577] mb-2">
                  {t('need_platinum.domain_limit_reached')}
                </p>
                <p className="text-sm text-gray-300">
                  {t('need_platinum.domain_limit_explanation', {
                    current: domainLimits?.max_domains || 0,
                  })}
                </p>
              </div>
            )}
          </DialogHeader>
          <div className="relative max-h-[calc(100vh-240px)] h-full overflow-auto">
            <PricingPage
              isModal={true}
              workspaceId={workspaceId}
              projectId={selectedProjectId}
              minRequiredPlan={derivedMinRequiredPlan}
              trigger={trigger}
              showEnterpriseCard={false}
              showDomainOptions={
                domainLimits &&
                !domainLimits.can_create_more &&
                domainLimits.max_domains > 0 &&
                (trigger === 'custom-domain' || trigger === 'buy-domain')
              }
            />
          </div>
        </div>
      </div>
    </DialogContent>
  )
}
